// A style module draws one atlas layer (design D4). It holds paint and the field labels; title,
// attribution, licence and as-of always come from the manifest entry, never from here.

import type { ExpressionSpecification, LayerSpecification } from "maplibre-gl";

export interface LegendClass {
  label: string;
  color: string;
}

export interface Field {
  key: string; // the raw property, so the panel can refuse owner fields whatever the label says
  label: string;
  value: string;
}

export interface Link {
  label: string;
  href: string;
}

export interface LayerStyle {
  id: string;
  /** Matches `tiles.minzoom/maxzoom` in layers.yaml; minzoom may be higher to spare mobile GPUs. */
  minzoom: number;
  maxzoom: number;
  layers(source: string): LayerSpecification[];
  legend: LegendClass[];
  /** Only the fields listed here are ever shown in the click panel. */
  describe(props: Record<string, unknown>): Field[];
  links?: Link[];
}

// Official maps for floodplain rows (design D7): destinations, not facts.
export const FEMA_MSC: Link = { label: "Official FEMA flood map", href: "https://msc.fema.gov/portal/search" };
export const CITY_FLOODPRO: Link = { label: "City of Austin FloodPro", href: "https://www.austintexas.gov/floodpro/" };

export const OTHER = "#797d85";

/**
 * A `match` on one property (or an expression over it) to class colours, falling back to OTHER.
 * MapLibre rejects a `match` whose labels mix strings and numbers, so keep one type per call.
 */
export function matchColor(input: string | ExpressionSpecification, classes: [value: string | number | string[] | number[], color: string][]): ExpressionSpecification {
  const expr: unknown[] = ["match", typeof input === "string" ? ["get", input] : input];
  for (const [value, color] of classes) expr.push(value, color);
  expr.push(OTHER);
  return expr as ExpressionSpecification;
}

/** Standard translucent fill plus a full-strength outline, so classes read on both basemaps. */
export function fillLayers(style: { id: string; minzoom: number; maxzoom: number }, source: string, color: ExpressionSpecification | string, opacity = 0.35): LayerSpecification[] {
  const base = { source, "source-layer": style.id, minzoom: style.minzoom };
  return [
    { id: `${style.id}:fill`, type: "fill", ...base, paint: { "fill-color": color, "fill-opacity": opacity } },
    { id: `${style.id}:line`, type: "line", ...base, paint: { "line-color": color, "line-width": ["interpolate", ["linear"], ["zoom"], 9, 0.5, 15, 1.5] } },
  ];
}

const EMPTY = new Set(["", "none", "null", "<null>"]);

/** Picks the listed fields that carry a value, in order, with plain labels. */
export function pick(props: Record<string, unknown>, spec: [key: string, label: string, format?: (v: unknown) => string][]): Field[] {
  const out: Field[] = [];
  for (const [key, label, format] of spec) {
    const raw = props[key];
    if (raw === undefined || raw === null || EMPTY.has(String(raw).trim().toLowerCase())) continue;
    out.push({ key, label, value: format ? format(raw) : String(raw) });
  }
  return out;
}

export const titleCase = (v: unknown) =>
  String(v)
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
